import { StylableButton } from "common/Buttons/Button.tsx";
import AppleOfferCodeModal from "features/settings/Subscription/AppleOfferCodeModal.tsx";
import CancelLastSubscriptionModal from "features/settings/Subscription/CancelLastSubscriptionModal.tsx";
import React, { useContext } from "react";
import { getAppleGoogleExpirationDate } from "@shared/features/settings/subscription/utils.ts";
import Text from "common/Typography/Text.tsx";
import { formatDate } from "@shared/utils/date";
import useWebViewCheckout from "features/settings/Subscription/useWebViewCheckout.ts";
import styled from "styled-components";
import { IS_APPLE_PAY } from "features/settings/common/constants.ts";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context";

interface WebViewCheckoutProps {
  loadingPaymentLink?: boolean;
  promoCodeRow?: React.JSX.Element;
  disabled?: boolean;
}

const WebViewCheckout = ({
  loadingPaymentLink,
  promoCodeRow,
  disabled,
}: WebViewCheckoutProps) => {
  const { isUpgrade, duration, plan } = useContext(BuySubscriptionContext)!;
  const {
    user,
    hidePromoCode,
    payButtonData,
    cancelLastSubscriptionModalProps,
    appleOfferCodeModalProps,
  } = useWebViewCheckout({ loadingPaymentLink });

  const getExpirationText = () => {
    const expirationDate = getAppleGoogleExpirationDate(
      user,
      duration,
      isUpgrade
    );
    if (isUpgrade) {
      return (
        <div>
          <div style={{ marginBottom: 12 }}>
            <Text>
              Upgrading now will start your new subscription immediately.
            </Text>
            {IS_APPLE_PAY && (
              <Text>
                Apple will charge the full amount for your new plan and
                automatically refund the unused portion of your current
                subscription.
              </Text>
            )}
          </div>
          <Text>
            Your plan will be upgraded to the {plan?.subscription_type} plan and
            will auto-renew on {expirationDate}.
          </Text>
        </div>
      );
    }
    return (
      <Text>
        Your plan will start{" "}
        {user?.isNativeSubscription()
          ? `on ${formatDate(user?.expiration_timestamp, false, true)}`
          : "today"}{" "}
        and will auto-renew on {expirationDate}.
      </Text>
    );
  };

  return (
    <>
      {getExpirationText()}
      {!hidePromoCode && (
        <PromoCodeRowWrapper>{promoCodeRow}</PromoCodeRowWrapper>
      )}
      <ActionButton
        onClick={payButtonData.onClick}
        $error={payButtonData.error}
        loading={payButtonData.loading}
        disabled={disabled}
      >
        {payButtonData.text}
      </ActionButton>
      <AppleOfferCodeModal {...appleOfferCodeModalProps} />
      <CancelLastSubscriptionModal {...cancelLastSubscriptionModalProps} />
    </>
  );
};

export default WebViewCheckout;

const ActionButton = styled(StylableButton).attrs({ type: "primary" })`
  width: 100%;
  margin-top: 16px;
`;
const PromoCodeRowWrapper = styled.div`
  margin-top: 8px;
`;
